import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { JobEvaluationListItem, JobEvaluationFilters } from "@/types/avaliacao";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { AvaliacaoCard } from "@/components/avaliacoes/AvaliacaoCard";
import { AvaliacaoDialog } from "@/components/avaliacoes/AvaliacaoDialog";
import { Card } from "@/components/ui/card";
import { AvaliacaoFilters } from "@/components/avaliacoes/AvaliacaoFilters";
import { usePageParam } from "@/hooks/use-page-param";
import { ListPagination } from "@/components/ui/list-pagination";
import { useQuery } from "@tanstack/react-query";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import { reportApiError } from "@/lib/report-error";

const FILTERS_STORAGE_KEY = "avaliacao_filters";
const DISPLAY_PER_PAGE_KEY = "avaliacoes_per_page";
const SEARCH_TERM_KEY = "avaliacoes_search_term";

export default function Avaliacoes() {
  const [filters, setFilters] = useState<JobEvaluationFilters>({});
  const [filteredCount, setFilteredCount] = useState(0);
  const [displayPerPage, setDisplayPerPage] = useState<number>(() => {
    const savedValue = localStorage.getItem(DISPLAY_PER_PAGE_KEY);
    return savedValue ? parseInt(savedValue, 10) : 12;
  });
  const [isCreatingEvaluations, setIsCreatingEvaluations] = useState(false);
  const [selectedAvaliacao, setSelectedAvaliacao] = useState<JobEvaluationListItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = usePageParam();

  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters);
        setFilters(parsedFilters);
      } catch (error) {
        console.error("Erro ao carregar filtros salvos:", error);
      }
    }
  }, []);

  // Query para buscar avaliações
  const { data: avaliacoes = [], isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["job-evaluations", filters],
    queryFn: async () => {
      // Primeiro, buscar todos os dados sem filtros de relacionamento
      let query = supabase
        .from("job_evaluations")
        .select(`
          id,
          controle_projeto_id,
          evaluator_type,
          status,
          rating,
          media_geral,
          rated_at,
          created_at,
          feedback_text,
          feedback_data,
          controle_projetos!inner(
            freelancer_id,
            agencia_id,
            data_inicio,
            data_conclusao,
            freelancers!inner(nome, email),
            agencias!inner(nome, email)
          )
        `)
        .is("deleted_at", null)
        // O deleted_at acima é o da avaliação; sem este a listagem mostra
        // avaliação de projeto que o Controle de Projetos já esconde.
        .is("controle_projetos.deleted_at", null)
        .order("created_at", { ascending: false });

      // Aplicar filtros diretos na tabela job_evaluations
      if (filters.evaluator_type) {
        query = query.eq("evaluator_type", filters.evaluator_type);
      }
      
      if (filters.status === "preenchido") {
        query = query.not("rating", "is", null);
      } else if (filters.status === "nao_preenchido") {
        query = query.is("rating", null);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      // Mapear os dados para o formato esperado
      let mappedData = (data || []).map((item: any) => ({
        id: item.id,
        controle_projeto_id: item.controle_projeto_id,
        freelancer_id: item.controle_projetos?.freelancer_id,
        agencia_id: item.controle_projetos?.agencia_id,
        evaluator_type: item.evaluator_type,
        status: item.status,
        rating: item.rating,
        media_geral: item.media_geral,
        rated_at: item.rated_at,
        created_at: item.created_at,
        freelancer_name: item.controle_projetos?.freelancers?.nome,
        agencia_name: item.controle_projetos?.agencias?.nome,
        project_start_date: item.controle_projetos?.data_inicio,
        project_end_date: item.controle_projetos?.data_conclusao,
        feedback_text: item.feedback_text,
        feedback_data: item.feedback_data,
        freelancer_email: item.controle_projetos?.freelancers?.email,
        agencia_email: item.controle_projetos?.agencias?.email,
      })) as JobEvaluationListItem[];

      // Aplicar filtros de relacionamento após o mapeamento
      if (filters.agencia_id) {
        mappedData = mappedData.filter(item => item.agencia_id === filters.agencia_id);
      }
      
      if (filters.freelancer_id) {
        mappedData = mappedData.filter(item => item.freelancer_id === filters.freelancer_id);
      }

      return mappedData;
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
  });

  const handleDisplayPerPageChange = (value: string) => {
    const numValue = parseInt(value, 10);
    setDisplayPerPage(numValue);
    localStorage.setItem(DISPLAY_PER_PAGE_KEY, value);
    setCurrentPage(1);
  };



  // Retornar todas as avaliações já filtradas pela query
  const filteredAvaliacoes = useMemo(() => {
    return avaliacoes;
  }, [avaliacoes]);

  // Atualizar contadores e verificar paginação
  useEffect(() => {
    if (filteredAvaliacoes) {
      setFilteredCount(filteredAvaliacoes.length);
      
      const totalFilteredPages = Math.ceil(filteredAvaliacoes.length / displayPerPage);
      if (currentPage > totalFilteredPages && totalFilteredPages > 0) {
        setCurrentPage(1);
      }
    }
  }, [filteredAvaliacoes, displayPerPage, currentPage, setCurrentPage]);

  // Calcular avaliações paginadas
  const totalFilteredPages = Math.ceil(filteredAvaliacoes.length / displayPerPage);
  
  const paginatedAvaliacoes = filteredAvaliacoes.slice(
    (currentPage - 1) * displayPerPage,
    currentPage * displayPerPage
  );

  const handleFiltersChange = (newFilters: JobEvaluationFilters) => {
    setFilters(newFilters);
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(newFilters));
    setCurrentPage(1);
  };
  
  const handleCreateEvaluations = async () => {
    setIsCreatingEvaluations(true);
    
    try {
      const result = await apiFetch<{ message: string }>(
        `${SUPABASE_URL}jobs/evaluations/generate-bulk`,
        {
          method: "POST",
          body: JSON.stringify({
            days_since_completion: 7,
            send_email_to_freelancer: true,
          }),
        },
      );

      toast({
        title: "Avaliações criadas com sucesso",
        description: result.message,
      });

      refetch();
    } catch (error) {
      reportApiError(error, { source: "avaliacoes", kind: "generate_bulk" });
      console.error("Erro ao criar avaliações:", error);
      toast({
        variant: "destructive",
        title: "Erro ao criar avaliações",
        description: "Não foi possível gerar novas avaliações. Tente novamente."
      });
    } finally {
      setIsCreatingEvaluations(false);
    }
  };

  const handleAvaliacaoClick = (avaliacao: JobEvaluationListItem) => {
    setSelectedAvaliacao(avaliacao);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedAvaliacao(null);
  };

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        title="Avaliações"
        subtitle={`${avaliacoes.length} avaliações cadastradas (mostrando ${filteredCount})`}
        actions={
          <Button
            onClick={handleCreateEvaluations}
            disabled={isCreatingEvaluations}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {isCreatingEvaluations ? "Gerando..." : "Gerar novas avaliações"}
          </Button>
        }
      />

      <AvaliacaoFilters 
        currentFilters={filters} 
        onFiltersChange={handleFiltersChange}
      />

      {isLoading || isRefetching ? (
        <div className="space-y-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={index} className="w-full h-[120px] animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {paginatedAvaliacoes.length > 0 ? (
              paginatedAvaliacoes.map(avaliacao => (
                <AvaliacaoCard
                  key={avaliacao.id}
                  avaliacao={avaliacao}
                  onClick={() => handleAvaliacaoClick(avaliacao)}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <p className="text-muted-foreground">
                  Nenhuma avaliação encontrada com os filtros aplicados.
                </p>
              </div>
            )}
          </div>

          {totalFilteredPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 mt-4">
              <div className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * displayPerPage) + 1}-{Math.min(currentPage * displayPerPage, filteredCount)} de {filteredCount} resultados
              </div>
              <ListPagination
                currentPage={currentPage}
                totalPages={totalFilteredPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <AvaliacaoDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        avaliacao={selectedAvaliacao}
        evaluationData={selectedAvaliacao ? {
          rating: selectedAvaliacao.rating,
          feedback_text: selectedAvaliacao.feedback_text,
          feedback_data: selectedAvaliacao.feedback_data,
          status: selectedAvaliacao.status
        } : undefined}
      />
    </div>
  );
}
